import type { MouseEvent } from 'react';
import { create } from 'zustand';
import type { CasePhoto } from '@/domain';

/**
 * What the image viewer is asked to show, from anywhere a photograph is. The
 * request of 5 October 2026 (third), section 6.
 *
 * One viewer for the whole application, drawn once by `PhotoViewerHost` in the
 * shell and opened through here, as a toast is. It is not drawn by the strip
 * that was tapped, nor through a portal from it: a strip sits inside a sheet
 * or a drawer that is transformed while it moves, which would confine a fixed
 * child to it, and a click inside a portal still bubbles through React to the
 * sheet's backdrop, which closes on any click that reaches it.
 */

/** One photograph to show. */
export type PhotoViewerItem =
  | {
      /** A photograph the lab has stored: its bytes are fetched, and it says who uploaded it and when. */
      kind: 'photo';
      photo: CasePhoto;
      /** What it shows: the stage, "Proof of work · Ceramic". */
      caption: string;
    }
  | {
      /** A photograph still on this device: an intake queue, a proof not yet attached. No uploader, no download. */
      kind: 'local';
      id: string;
      file: Blob;
      caption: string;
    };

export interface PhotoViewerRequest {
  items: readonly PhotoViewerItem[];
  /** Which one to open on. */
  startIndex: number;
  /** Where the tile that was tapped is, for the photograph to grow out of. */
  origin: DOMRect | null;
  /** Where the tile of each photograph is now, for the one on show to shrink back into. */
  originOf?: (index: number) => DOMRect | null;
  /** The case, for the name of a downloaded file. */
  jobOrderNo?: string | null;
}

interface PhotoViewerState {
  request: PhotoViewerRequest | null;
  /** Counted up on every open, so the same photographs opened twice are a new viewer. */
  nonce: number;
  open: (request: PhotoViewerRequest) => void;
  close: () => void;
}

export const usePhotoViewerStore = create<PhotoViewerState>((set) => ({
  request: null,
  nonce: 0,
  open: (request) =>
    set((state) => (request.items.length === 0 ? state : { request, nonce: state.nonce + 1 })),
  close: () => set({ request: null }),
}));

/** The id a photograph goes by in the viewer and on its tile. */
export function photoViewerItemId(item: PhotoViewerItem): string {
  return item.kind === 'photo' ? item.photo.id : item.id;
}

/** Opens the viewer. Callable from outside React, as `toast` is. Nothing opens for an empty list. */
export function openPhotoViewer(request: PhotoViewerRequest): void {
  usePhotoViewerStore.getState().open(request);
}

export function closePhotoViewer(): void {
  usePhotoViewerStore.getState().close();
}

/**
 * The marks a tile carries so the viewer can find it again, written on the
 * elements as `data-photo-tiles`, `data-photo-tile="<id>"` and
 * `data-photo-frame`: the strip its photographs belong to, the tile of one
 * photograph, and the picture inside the tile, which is what the photograph
 * grows out of and shrinks back into (the tile itself also holds a caption).
 */
const PHOTO_TILES = 'data-photo-tiles';
const PHOTO_TILE = 'data-photo-tile';
const PHOTO_FRAME = 'data-photo-frame';

function frameRect(tile: Element): DOMRect {
  return (tile.querySelector(`[${PHOTO_FRAME}]`) ?? tile).getBoundingClientRect();
}

/** Where the tile of a photograph is now within a strip; null when it has gone from the screen. */
export function photoTileRect(strip: Element, id: string): DOMRect | null {
  if (!strip.isConnected) return null;
  for (const tile of strip.querySelectorAll(`[${PHOTO_TILE}]`)) {
    if (tile.getAttribute(PHOTO_TILE) === id) return frameRect(tile);
  }
  return null;
}

/**
 * Opens the viewer from the tile that was tapped: on `items` at `index`,
 * growing out of that tile, and shrinking back into the tile of whichever
 * photograph is on show when it closes.
 *
 * The tiles are looked for inside the nearest strip (`data-photo-tiles`), so
 * a photograph that is on the screen twice goes back to where it was opened
 * from. A control that stands for several photographs and is the tile of none
 * (the marker on a history row) is itself where they all come from and go to.
 */
export function openPhotoTile(
  event: MouseEvent<HTMLElement>,
  items: readonly PhotoViewerItem[],
  index: number,
  jobOrderNo?: string | null,
): void {
  const tile = event.currentTarget;
  const strip = tile.closest(`[${PHOTO_TILES}]`);
  // Safari does not focus a button it is clicked on, and focus goes back to the opener on closing.
  tile.focus({ preventScroll: true });

  openPhotoViewer({
    items,
    startIndex: index,
    origin: frameRect(tile),
    originOf: (at) => {
      const item = items[at];
      if (item === undefined) return null;
      if (strip === null) return tile.isConnected ? frameRect(tile) : null;
      return photoTileRect(strip, photoViewerItemId(item));
    },
    jobOrderNo: jobOrderNo ?? null,
  });
}
